import { QueryClient, QueryFunction } from "@tanstack/react-query";

class ApiError extends Error {
  public status: number;
  public statusText: string;
  public data?: any;

  constructor(status: number, statusText: string, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

// Enhanced error handling function with improved error information
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // Try to parse response as JSON first
    let errorData;
    let errorText;

    try {
      // Clone the response before attempting to read it
      const clone = res.clone();
      errorData = await clone.json();
      errorText = errorData.message || errorData.error || res.statusText;
    } catch (e) {
      // If JSON parsing fails, fallback to text
      errorText = (await res.text()) || res.statusText;
    }

    throw new ApiError(
      res.status,
      res.statusText,
      `${res.status}: ${errorText}`,
      errorData,
    );
  }
}

// Export ApiError for use in components
export { ApiError };

// Enhanced API request function with improved error handling and performance optimizations
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: RequestInit,
): Promise<Response> {
  try {
    // Get auth token for Bearer authentication (fallback to session cookies)
    let authToken = null;
    try {
      if (typeof window !== "undefined" && window.__AUTH_TOKEN) {
        authToken = window.__AUTH_TOKEN;
      }
    } catch (e) {
      console.warn("Could not access auth token for API request:", e);
    }

    // Build headers object
    const headers: Record<string, string> = {};

    // Add Content-Type for requests with data
    if (data) {
      headers["Content-Type"] = "application/json";
      headers["Cache-Control"] = method === "GET" ? "max-age=3600" : "no-cache";
    }

    // Add Authorization header if we have a token
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    const defaultOptions: RequestInit = {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include" as RequestCredentials, // Keep session cookies as fallback
    };

    // Merge with any additional options provided, handling headers specially
    const mergedOptions: RequestInit = {
      ...defaultOptions,
      ...options,
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
    };

    const res = await fetch(url, mergedOptions);

    // Enhanced error handling
    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    if (error instanceof ApiError) {
      // Already properly formatted error
      throw error;
    } else if (error instanceof Error) {
      // Network or other JS errors
      console.error(`API Request failed for ${method} ${url}:`, error);
      throw new ApiError(
        0,
        "Network Error",
        `Network error while fetching ${url}: ${error.message}`,
        { originalError: error.message },
      );
    } else {
      // Unknown error type
      console.error(
        `Unknown error in API request for ${method} ${url}:`,
        error,
      );
      throw new ApiError(
        0,
        "Unknown Error",
        `Unknown error while fetching ${url}`,
        { originalError: error },
      );
    }
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";

// Cache for storing responses to avoid duplicate requests
const responseCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
  cacheTime?: number;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior, cacheTime = CACHE_TTL }) =>
  async ({ queryKey, signal }: { queryKey: any; signal?: AbortSignal }) => {
    try {
      const cacheKey = Array.isArray(queryKey)
        ? queryKey.join("|")
        : String(queryKey);

      // Check if we have a valid cached response
      const cachedResponse = responseCache.get(cacheKey);
      if (cachedResponse && Date.now() - cachedResponse.timestamp < cacheTime) {
        return cachedResponse.data;
      }

      // Get auth token from the auth context if available
      // Note: This is imported dynamically to avoid circular dependencies
      let authToken = null;
      try {
        // Try to get the auth context from window if available (React context won't be available here)
        if (typeof window !== "undefined" && window.__AUTH_TOKEN) {
          authToken = window.__AUTH_TOKEN;
        }
      } catch (e) {
        console.warn("Could not access auth token for API request:", e);
      }

      // Add performance headers and auth token to requests
      const headers: Record<string, string> = {
        Accept: "application/json",
        "Cache-Control": "max-age=3600",
      };

      // Add Authorization header if we have a token
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const fetchOptions: RequestInit = {
        credentials: "include" as RequestCredentials, // Keep cookies for session
        signal,
        headers,
      };

      const res = await fetch(queryKey[0] as string, fetchOptions);

      // Handle 401 Unauthorized based on configuration
      if (res.status === 401) {
        let responseBody: any = null;
        try {
          // Try to parse the response to check if token is expired
          responseBody = await res.clone().json();
        } catch (e) {
          // Ignore JSON parsing errors
        }

        const isTokenExpired =
          responseBody?.expired === true ||
          responseBody?.code === "token_expired";

        if (isTokenExpired) {
          console.log("Token is expired, attempting to refresh...");
        } else {
          console.log(
            "Request failed with 401, attempting to refresh token...",
          );
        }

        // Try to refresh the token
        try {
          if (typeof window !== "undefined" && window.__REFRESH_AUTH_TOKEN) {
            // Call the refresh function if available, force refresh for expired tokens
            const newToken = await window.__REFRESH_AUTH_TOKEN();

            if (newToken) {
              console.log(
                "Token refreshed successfully, retrying request with new token",
              );

              // Add the new token to the headers
              headers["Authorization"] = `Bearer ${newToken}`;

              // Retry the request with the new token
              const retryRes = await fetch(queryKey[0] as string, {
                ...fetchOptions,
                headers,
              });

              if (retryRes.ok) {
                const retryData = await retryRes.json();

                // Cache the successful response
                responseCache.set(cacheKey, {
                  data: retryData,
                  timestamp: Date.now(),
                });

                return retryData;
              }

              // If retry still fails with 401 and we're configured to return null
              if (
                unauthorizedBehavior === "returnNull" &&
                retryRes.status === 401
              ) {
                console.log(
                  "Request still failed after token refresh, returning null",
                );
                return null;
              }

              await throwIfResNotOk(retryRes);
            } else {
              console.log(
                "Token refresh returned null, user may be signed out",
              );
            }
          } else {
            console.log("No refresh token function available on window");
          }
        } catch (refreshError) {
          console.error("Error refreshing token:", refreshError);

          // If token refresh completely fails, we might need to force a sign out or reload
          if (isTokenExpired && typeof window !== "undefined") {
            console.log(
              "Token refresh failed for expired token, reloading page...",
            );

            // Store current URL to redirect back after authentication
            try {
              sessionStorage.setItem(
                "auth_redirect",
                window.location.pathname + window.location.search,
              );
            } catch (e) {
              // Ignore storage errors
            }

            // Reload page after a short delay to allow logs to be sent
            setTimeout(() => {
              window.location.href = "/auth/login?expired=true";
            }, 100);
          }
        }

        // If token refresh failed or not available and we're configured to return null
        if (unauthorizedBehavior === "returnNull") {
          return null;
        }
      }

      await throwIfResNotOk(res);
      const data = await res.json();

      // Cache the response
      responseCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      return data;
    } catch (error) {
      // Only log non-abort errors to avoid noise from normal component unmounting
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error(`Query failed for ${JSON.stringify(queryKey)}:`, error);
      } else if (process.env.NODE_ENV === 'development' && error instanceof Error && error.name === 'AbortError') {
        console.debug(`Query cancelled for ${JSON.stringify(queryKey)}:`, error.message);
      }

      // Handle 401 Unauthorized if that's what happened
      if (
        error instanceof ApiError &&
        error.status === 401 &&
        unauthorizedBehavior === "returnNull"
      ) {
        return null;
      }

      // Re-throw all other errors for proper handling by React Query
      throw error;
    }
  };

// Clean up expired cache entries periodically
setInterval(() => {
  const now = Date.now();
  responseCache.forEach((value, key) => {
    if (now - value.timestamp > CACHE_TTL) {
      responseCache.delete(key);
    }
  });
}, 60000); // Clean up every minute

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: true, // Enable this to ensure data is fresh when user returns to app
      staleTime: 5 * 60 * 1000, // 5 minutes stale time
      gcTime: 10 * 60 * 1000, // 10 minutes cache time
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx client errors, only on network failures and 5xx server errors
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false;
        }
        // Allow up to 3 retries for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});
